import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildProgramMap,
  loadSoundfontBytes,
  type SoundfontSource,
} from "@midi-fantom-utils/player-core";

export type ProgramMapData = {
  /** Мелодические инструменты (program 0..127 с непустым `getMelodic`). */
  melodic: ReadonlyArray<{ program: number; name: string }>;
  /** Имя ударной установки из soundfont (или null, если kit отсутствует). */
  drumKit: string | null;
  /** Обратная карта «имя пресета → program». При дублях имён — первый program. */
  reverse: ReadonlyMap<string, number>;
};

export type UseProgramMapResult = ProgramMapData & {
  loading: boolean;
  error: string | null;
};

const EMPTY: ProgramMapData = {
  melodic: [],
  drumKit: null,
  reverse: new Map(),
};

const sourceKey = (source: SoundfontSource): string =>
  `${source.kind}:${source.url}`;

/**
 * Лениво загружает `ProgramMap` текущего soundfont: начинает загрузку только
 * когда `active === true` (аккордион «Каналы» открыт). Кеширует результат по
 * `source` в useRef, поэтому повторные открытия/рендеры не перезагружают байты;
 * смена `source` (settings.soundfont) автоматически инвалидирует кеш — новая
 * запись строится под новый источник.
 */
export const useProgramMap = (
  source: SoundfontSource | null,
  active: boolean
): UseProgramMapResult => {
  const [state, setState] = useState<{ data: ProgramMapData; loading: boolean; error: string | null }>({
    data: EMPTY,
    loading: false,
    error: null,
  });
  const cacheRef = useRef<Map<string, ProgramMapData>>(new Map());

  useEffect(() => {
    if (!source || !active) return;
    const key = sourceKey(source);
    const cached = cacheRef.current.get(key);
    if (cached) {
      setState({ data: cached, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: EMPTY, loading: true, error: null });
    (async () => {
      try {
        const bytes = await loadSoundfontBytes(source);
        const programMap = buildProgramMap(bytes);
        const melodic: Array<{ program: number; name: string }> = [];
        const reverse = new Map<string, number>();
        for (let program = 0; program <= 127; program += 1) {
          const name = programMap.getMelodic(program);
          if (name) {
            melodic.push({ program, name });
            // При дублях имён пресетов (часто в GeneralUser GS) берём первый.
            if (!reverse.has(name)) reverse.set(name, program);
          }
        }
        const data: ProgramMapData = {
          melodic,
          drumKit: programMap.getDrumKit(),
          reverse,
        };
        cacheRef.current.set(key, data);
        if (!cancelled) setState({ data, loading: false, error: null });
      } catch (err) {
        if (!cancelled) {
          setState({
            data: EMPTY,
            loading: false,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source, active]);

  return useMemo(
    () => ({
      ...state.data,
      loading: state.loading,
      error: state.error,
    }),
    [state]
  );
};
